import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Tag,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  MailOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  passwordCreate,
  passwordDelete,
  passwordsGet,
  passwordSendCredentials,
  passwordUpdate,
  type PrivatePassword,
  type PrivatePasswordInput,
} from '@/services/treasure';
import styles from './PasswordsTab.module.css';

interface Props {
  mobile?: boolean;
}

function PasswordsTab({ mobile }: Props) {
  const [passwords, setPasswords] = useState<PrivatePassword[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PrivatePassword | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    passwordsGet()
      .then(setPasswords)
      .catch(() => setPasswords([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    passwords.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set);
  }, [passwords]);

  const filtered = useMemo(() => {
    let list = passwords;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.serviceUrl?.toLowerCase().includes(q) ||
          p.username?.toLowerCase().includes(q),
      );
    }
    if (tagFilter.length > 0) {
      list = list.filter((p) =>
        tagFilter.every((t) => (p.tags || []).includes(t)),
      );
    }
    return list;
  }, [passwords, search, tagFilter]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (p: PrivatePassword) => {
    setEditing(p);
    form.setFieldsValue({
      name: p.name,
      serviceUrl: p.serviceUrl,
      username: p.username,
      password: p.password,
      tags: p.tags,
      remark: p.remark,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    try {
      const data: PrivatePasswordInput = {
        name: values.name || '',
        serviceUrl: values.serviceUrl || '',
        username: values.username || '',
        password: values.password || '',
        tags: values.tags || [],
        remark: values.remark || '',
      };
      if (editing) {
        await passwordUpdate(editing.id, data);
        message.success('已更新');
      } else {
        await passwordCreate(data);
        message.success('已添加');
      }
      setModalOpen(false);
      load();
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await passwordDelete(id);
      message.success('已删除');
      load();
    } catch {
      message.error('删除失败');
    }
  };

  const handleSend = () => {
    Modal.confirm({
      title: '发送私人密码',
      content: '系统将向您的注册邮箱发送私人密码清单（含明文密码）。确认发送？',
      okText: '确认发送',
      cancelText: '取消',
      onOk: async () => {
        setSending(true);
        try {
          const r = await passwordSendCredentials();
          if (r.sent) message.success(`密码清单已发送至 ${r.email}`);
          else message.warning(`发送失败：${r.error || '平台未配置邮件服务'}`);
        } catch {
          message.error('发送失败，请稍后再试');
        } finally {
          setSending(false);
        }
      },
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <Input
          prefix={<SearchOutlined style={{ color: 'rgba(0,255,255,0.4)' }} />}
          placeholder="搜索名称 / 地址 / 用户名"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className={styles.searchInput}
        />
        {allTags.length > 0 && (
          <Select
            mode="multiple"
            allowClear
            placeholder="按标签筛选"
            value={tagFilter}
            onChange={setTagFilter}
            className={styles.tagSelect}
            options={allTags.map((t) => ({ value: t, label: t }))}
            maxTagCount="responsive"
          />
        )}
        <Button
          icon={<PlusOutlined />}
          className={styles.addBtn}
          onClick={openAdd}
        >
          {!mobile && '添加密码'}
        </Button>
      </div>

      {loading ? (
        <div className={styles.loadingArea}><Spin /></div>
      ) : filtered.length === 0 ? (
        <div className={styles.emptyArea}>
          {passwords.length === 0 ? '暂无私人密码，点击右上角添加' : '没有匹配的记录'}
        </div>
      ) : (
        <div className={styles.list}>
          {filtered.map((p) => (
            <div key={p.id} className={styles.card}>
              <div className={styles.cardTop}>
                <div className={styles.cardTitle}>
                  <LockOutlined className={styles.lockIcon} />
                  <span>{p.name || '未命名'}</span>
                </div>
                <div className={styles.cardActions}>
                  <EditOutlined className={styles.actionIcon} onClick={() => openEdit(p)} />
                  <Popconfirm
                    title="确认删除此密码？"
                    okText="删除"
                    cancelText="取消"
                    onConfirm={() => handleDelete(p.id)}
                  >
                    <DeleteOutlined className={`${styles.actionIcon} ${styles.deleteIcon}`} />
                  </Popconfirm>
                </div>
              </div>

              {p.serviceUrl && (
                <div className={styles.row}>
                  <span className={styles.label}>服务地址</span>
                  <code className={styles.value}>{p.serviceUrl}</code>
                </div>
              )}
              {p.username && (
                <div className={styles.row}>
                  <span className={styles.label}>用户名</span>
                  <code className={styles.value}>{p.username}</code>
                </div>
              )}
              {p.password && (
                <div className={styles.row}>
                  <span className={styles.label}>密码</span>
                  <code className={styles.value}>••••••••</code>
                </div>
              )}
              {p.tags && p.tags.length > 0 && (
                <div className={styles.tagRow}>
                  {p.tags.map((t) => (
                    <Tag
                      key={t}
                      className={styles.tag}
                      onClick={() => setTagFilter((prev) => prev.includes(t) ? prev : [...prev, t])}
                    >
                      {t}
                    </Tag>
                  ))}
                </div>
              )}
              {p.remark && (
                <div className={styles.remark}>{p.remark}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={styles.sendArea}>
        <Button
          icon={<MailOutlined />}
          loading={sending}
          className={styles.sendBtn}
          onClick={handleSend}
          block={mobile}
        >
          发送私人密码到邮箱
        </Button>
      </div>

      <Modal
        title={editing ? '编辑密码' : '添加密码'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={saving ? '保存中…' : '保存'}
        cancelText="取消"
        confirmLoading={saving}
        width={mobile ? '92vw' : 520}
        style={{ top: mobile ? 40 : undefined }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="name" label="密码名称">
            <Input placeholder="例如：GitHub 主账号" />
          </Form.Item>
          <Form.Item name="serviceUrl" label="服务地址">
            <Input placeholder="例如：https://github.com" />
          </Form.Item>
          <Form.Item name="username" label="用户名">
            <Input placeholder="登录用户名或邮箱" />
          </Form.Item>
          <Form.Item name="password" label="密码">
            <Input.Password placeholder="密码" />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <Select
              mode="tags"
              placeholder="输入后回车添加标签"
              tokenSeparators={[',']}
            />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="备注信息（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default PasswordsTab;
